import { useQuery, useQueryClient } from "react-query";
import { CATEGORIES, USERS } from "../../config/apiConfig";
import axios from "../../config/axiosInstance";
import { Pagination } from "flowbite-react";
import { useApplication } from "../../contexts/ApplicationContext";
import { useState } from "react";
import { IoMdAdd } from "react-icons/io";
import CardLoading from "../CardLoading";
import { getFirstLettersName } from "../../utils/utils";
import { TbLockSearch } from "react-icons/tb";
import UserQueriesForm from "../forms/UserQueriesForm";
import UserForm from "../forms/UserForm";
import { MdEmail, MdNumbers } from "react-icons/md";
import { FiEdit2, FiTrash2, FiX } from "react-icons/fi";
import ModalDelete from "../modals/ModalDelete";

const UsersTable = () => {
  const { userPermission } = useApplication();
  const [currentPage, setCurrentPage] = useState(1);
  const queryClient = useQueryClient();
  const [isDrawerPermissionQueries, setIsDrawerPermissionQueries] =
    useState(false);
  const [isDrawerOpenUser, setIsDrawerOpenUser] = useState(false);
  const [isOpenModalDelete, setOpenModalDelete] = useState(false);
  const [isLoadingDelete, setIsLoadingDelete] = useState(false);

  const [totalPages, setTotalPages] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [typeDrawer, setTypeDrawer] = useState("CREATE");
  const onPageChange = (page) => setCurrentPage(page);
  const {
    isLoading,
    error,
    data: users,
  } = useQuery(["users", currentPage], async () => {
    const { data: items } = await axios.get(
      `${USERS.GET_USERS}?page=${currentPage}&excludeUserType=REPRESENTATIVE`,
    );
    setTotalPages(items.data.totalPages || 1);
    return items.data.data;
  });

  const {
    isLoadingCategories,
    error: errorCategories,
    data: categories,
  } = useQuery(["categories", currentPage], async () => {
    const { data } = await axios.get(
      `${CATEGORIES.GET_ALL}?page=${currentPage}`,
    );

    return data.data.data;
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSelectedUser((prevProfessional) => ({
      ...prevProfessional,
      [name]: value,
    }));
  };

  const handleAddProfessional = () => {
    setSelectedUser(null);
    setTypeDrawer("CREATE");
    setIsDrawerOpenUser((prevState) => !prevState);
  };

  const handleEditUserQueriesPermission = (user) => {
    setSelectedUser(user);
    setIsDrawerPermissionQueries((prevState) => !prevState);
  };

  const handleEditUser = (user) => {
    setTypeDrawer("EDIT");
    setSelectedUser(user);
    setIsDrawerOpenUser((prevState) => !prevState);
  };

  const handleDeleteUser = (professional) => {
    setSelectedUser(professional);
    setOpenModalDelete(true);
  };

  const deleteUser = async () => {
    try {
      setIsLoadingDelete(true);
      await axios.delete(`${USERS.GET_USER}/${selectedUser.id}`);

      queryClient.invalidateQueries(["users", currentPage]);
      setOpenModalDelete(false);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingDelete(false);
    }
  };

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
            Gestão
          </p>
          <h1 className="text-lg font-bold text-gray-900 mt-0.5">Usuários</h1>
        </div>
        {userPermission !== "USER" && (
          <>
            {/* Desktop button */}
            <button
              type="button"
              disabled={isLoading}
              onClick={handleAddProfessional}
              className="hidden md:flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-700 transition disabled:opacity-50"
            >
              <IoMdAdd size={16} />
              Adicionar usuário
            </button>

            {/* Mobile FAB */}
            <button
              type="button"
              disabled={isLoading}
              onClick={handleAddProfessional}
              className="md:hidden fixed bottom-6 right-6 z-40 flex h-13 w-13 items-center justify-center rounded-full bg-gray-900 text-white shadow-lg active:scale-95 transition disabled:opacity-50"
              style={{ width: 52, height: 52 }}
            >
              <IoMdAdd size={22} />
            </button>
          </>
        )}
      </div>

      {/* ── Mobile: cards ── */}
      <div className="flex flex-col gap-3 md:hidden">
        {isLoading
          ? Array.from({ length: 6 }).map((_, i) => <CardLoading key={i} />)
          : users.map((user, index) => (
              <div
                key={index}
                className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden"
              >
                <div
                  className={`h-1 w-full ${user?.isActive ? "bg-emerald-400" : "bg-gray-200"}`}
                />
                <div className="p-4 flex flex-col gap-3">
                  <div className="flex items-center gap-3">
                    <Avatar user={user} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-gray-900">
                        {user?.name}
                      </p>
                      <div className="mt-1 flex flex-wrap gap-1">
                        <StatusBadge isActive={user?.isActive} />
                        <PermissionBadge permissionId={user?.permissionId} />
                      </div>
                    </div>
                    <ActionButtons
                      user={user}
                      users={users}
                      userPermission={userPermission}
                      onEdit={handleEditUser}
                      onDelete={handleDeleteUser}
                      onPermission={handleEditUserQueriesPermission}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5 pt-2 border-t border-gray-100">
                    <div className="flex items-center gap-2 text-gray-400">
                      <MdEmail size={13} className="shrink-0" />
                      <span className="truncate text-xs">{user.email}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-400">
                      <MdNumbers size={13} className="shrink-0" />
                      <span className="text-xs">
                        Cód.{" "}
                        <span className="font-medium text-gray-700">
                          {user.accessCode || "—"}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
      </div>

      {/* ── Desktop: table ── */}
      <div className="hidden md:block bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50">
              <th className="py-3 pl-5 pr-4 text-left">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                  Usuário
                </span>
              </th>
              <th className="py-3 pr-4 text-left">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                  E-mail
                </span>
              </th>
              <th className="py-3 pr-4 text-left">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                  Cód. acesso
                </span>
              </th>
              <th className="py-3 pr-4 text-left">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                  Status
                </span>
              </th>
              <th className="py-3 pr-5 text-right">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                  Ações
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading
              ? Array.from({ length: 8 }).map((_, i) => (
                  <tr
                    key={i}
                    className="border-b border-gray-100 last:border-none"
                  >
                    <td colSpan={5} className="py-3 pl-5">
                      <div className="h-4 w-full rounded-md bg-gray-100 animate-pulse" />
                    </td>
                  </tr>
                ))
              : users.map((user, index) => (
                  <tr
                    key={index}
                    className="border-b border-gray-100 last:border-none hover:bg-gray-50 transition-colors"
                  >
                    {/* Avatar + name + permission */}
                    <td className="py-3 pl-5 pr-4">
                      <div className="flex items-center gap-3">
                        <Avatar user={user} size="sm" />
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">
                            {user?.name}
                          </p>
                        </div>
                      </div>
                    </td>

                    {/* Email */}
                    <td className="py-3 pr-4 text-xs text-gray-500 max-w-[200px]">
                      <span className="truncate block">{user.email}</span>
                    </td>

                    {/* Access code */}
                    <td className="py-3 pr-4 text-xs font-medium text-gray-700">
                      {user.accessCode || (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>

                    {/* Status */}
                    <td className="py-3 pr-4">
                      <StatusBadge isActive={user?.isActive} />
                    </td>

                    {/* Actions */}
                    <td className="py-3 pr-5 text-right">
                      <ActionButtons
                        user={user}
                        users={users}
                        userPermission={userPermission}
                        onEdit={handleEditUser}
                        onDelete={handleDeleteUser}
                        onPermission={handleEditUserQueriesPermission}
                      />
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-5">
          <Pagination
            layout="pagination"
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            previousLabel=""
            nextLabel=""
            showIcons
          />
        </div>
      )}

      {/* Modal: Create / Edit user */}
      {isDrawerOpenUser && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4"
          style={{
            background: "rgba(0,0,0,0.45)",
            backdropFilter: "blur(2px)",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsDrawerOpenUser(false);
          }}
        >
          <div className="bg-white w-full sm:rounded-2xl shadow-2xl sm:max-w-2xl max-h-[92vh] flex flex-col rounded-t-2xl">
            <div className="flex items-center justify-between px-6 pt-3 pb-2 border-b border-gray-100 shrink-0">
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                  {typeDrawer === "CREATE" ? "Novo usuário" : "Editar usuário"}
                </p>
                <h2 className="text-base font-bold text-gray-900 mt-0.5">
                  {typeDrawer === "CREATE"
                    ? "Criar usuário"
                    : selectedUser?.name?.split(" ")[0]}
                </h2>
              </div>
              <button
                onClick={() => setIsDrawerOpenUser(false)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition"
              >
                <FiX size={18} />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 px-6 py-5">
              <UserForm
                user={selectedUser}
                onClose={setIsDrawerOpenUser}
                isOpen={isDrawerOpenUser}
                type={typeDrawer}
                fetchUsers={() => queryClient.invalidateQueries(["users"])}
              />
            </div>
          </div>
        </div>
      )}

      <ModalDelete
        handleDelete={deleteUser}
        isOpen={isOpenModalDelete}
        setOpenModal={setOpenModalDelete}
        title="Tem certeza que deseja excluir esse usuário?"
        isLoading={isLoadingDelete}
      />
    </>
  );
};

const PERMISSION_LABELS = {
  1: { label: "Admin", color: "bg-violet-100 text-violet-700" },
  2: { label: "Power", color: "bg-blue-100 text-blue-700" },
  3: { label: "User", color: "bg-gray-100 text-gray-600" },
};

const Avatar = ({ user, size = "md" }) => {
  const dim = size === "sm" ? "h-8 w-8 text-xs" : "h-10 w-10 text-sm";
  const initials = getFirstLettersName(user?.name || "");
  if (user?.photo) {
    return (
      <img
        src={user.photo}
        alt={user.name}
        className={`${dim} rounded-full object-cover shrink-0`}
      />
    );
  }
  return (
    <div
      className={`${dim} rounded-full bg-gray-900 text-white font-semibold flex items-center justify-center shrink-0 select-none`}
    >
      {initials}
    </div>
  );
};

const StatusBadge = ({ isActive }) => (
  <span
    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${
      isActive ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"
    }`}
  >
    {isActive ? "Ativo" : "Inativo"}
  </span>
);

const PermissionBadge = ({ permissionId }) => {
  const { label, color } = PERMISSION_LABELS[permissionId] || {
    label: "—",
    color: "bg-gray-100 text-gray-400",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${color}`}
    >
      {label}
    </span>
  );
};

const ActionButtons = ({
  user,
  users,
  userPermission,
  onEdit,
  onDelete,
  onPermission,
}) => {
  const canManage = userPermission !== "USER";
  const isLastAdmin =
    users?.filter((u) => u.permissionId === 1).length === 1 &&
    user.permissionId === 1;

  return (
    <div className="flex items-center gap-1 justify-end">
      <button
        title="Editar"
        onClick={() => onEdit(user)}
        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition"
      >
        <FiEdit2 size={14} />
      </button>
      {canManage && !isLastAdmin && (
        <button
          title="Excluir"
          onClick={() => onDelete(user)}
          className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition"
        >
          <FiTrash2 size={14} />
        </button>
      )}
    </div>
  );
};

export default UsersTable;
